import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import FilterDrawer from '@/enterprise/components/FilterDrawer';
import DateRangePicker from '@/enterprise/components/InvoiceCentral/shared/DateRangePicker';
import { validateNumber } from '@/enterprise/components/InvoiceCentral/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';

export interface EligibleInvoiceFilterOptions {
  originalDueDate?: {
    from?: string | null;
    to?: string | null;
  };
  // Pay Date
  paymentSent?: {
    from?: string | null;
    to?: string | null;
  };
  // Good Funds Date
  paymentDeposit?: {
    from?: string | null;
    to?: string | null;
  };
  amount?: {
    from?: number | null;
    to?: number | null;
  };
  // Taker Maker ID
  takerMakerId?: string | null;
}

const EligibleInvoiceFilters = ({
  filterOpen,
  setFilterOpen,
  handleFilters,
  currency,
  filters,
  takerMarkets,
}: {
  filterOpen: boolean;
  setFilterOpen: (open: boolean) => void;
  handleFilters: (filters: EligibleInvoiceFilterOptions) => void;
  currency: string;
  filters?: EligibleInvoiceFilterOptions;
  takerMarkets: TakerMarket[];
}) => {
  const { t } = useTranslation();
  const { amount, originalDueDate, paymentSent, paymentDeposit, takerMakerId } = filters ?? {};

  const takerMakerIds = Array.from(new Set(takerMarkets.map((tm) => tm.takerMakerId)));

  // useUrlState decodes digit-only strings as numbers (e.g. "332913977" → 332913977),
  // so normalize to string for Select value matching against the takerMakerId string options.
  const resolvedTakerMakerId = takerMakerId ? String(takerMakerId) : null;

  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState<'allDates' | 'originalDueDate' | 'paymentSent' | 'paymentDeposit'>(
    originalDueDate ? 'originalDueDate' : paymentSent ? 'paymentSent' : paymentDeposit ? 'paymentDeposit' : 'allDates'
  );

  // Local state for debounced input values for amount filters
  const [amountFromDebounce, setAmountFromDebounce] = useState<string>(amount?.from?.toString() || '');
  const [amountToDebounce, setAmountToDebounce] = useState<string>(amount?.to?.toString() || '');

  const hasIndiaDivisions = useUserHasIndiaDivisions();

  // If date radio was selected but no From or To date selected, reset to All Dates radio on close
  useEffect(() => {
    if (!filterOpen && !originalDueDate && !paymentSent && !paymentDeposit) {
      setSelectedDate('allDates');
    }
  }, [filterOpen, originalDueDate, paymentSent, paymentDeposit]);

  useEffect(() => {
    // Sync local amount values when urlState filters change outside of filter drawer (e.g. Clear filters)
    if (!filterOpen) {
      setAmountFromDebounce(amount?.from?.toString() || '');
      setAmountToDebounce(amount?.to?.toString() || '');
    }
  }, [amount?.from, amount?.to, filterOpen]);

  useDebounce(
    () => {
      // Debounce call to update urlState when filter is open and user is typing
      // Checking for open filter prevents circular updates if user clicks Clear filters which updates local amount values from urlState
      if (filterOpen) {
        handleFilters({ amount: { from: amountFromDebounce === '' ? null : Number(amountFromDebounce) } });
      }
    },
    500,
    [amountFromDebounce]
  );

  useDebounce(
    () => {
      if (filterOpen) {
        handleFilters({ amount: { to: amountToDebounce === '' ? null : Number(amountToDebounce) } });
      }
    },
    500,
    [amountToDebounce]
  );

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        {/* Division Filter */}
        {takerMakerIds.length > 1 && (
          <div className="w-full space-y-1">
            <Label>{t('core.division')}</Label>
            <Select
              key={`division-select${resolvedTakerMakerId ? `-${resolvedTakerMakerId}` : ''}`}
              onValueChange={(value) => handleFilters({ takerMakerId: value === 'all' ? null : value })}
              value={resolvedTakerMakerId ?? 'all'}
            >
              <SelectTrigger id="division">
                <SelectValue placeholder={t('core.selectDivision')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('invoiceCentral.allDivisions')}</SelectItem>
                {takerMakerIds.map((id) => (
                  <SelectItem key={id} value={id}>
                    {id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Invoice Deposit Date Filters */}
        <div className="w-full space-y-1">
          <Label>{t('core.dates')}</Label>
          <div className="rounded border border-stroke bg-canvas p-4">
            <ul className="space-y-2">
              {/* All Deposit Dates i.e. no date filter applied */}
              <li>
                <label htmlFor="allDates" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="allDates"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'allDates'}
                    onChange={() => {
                      setSelectedDate('allDates');
                      handleFilters({
                        originalDueDate: { from: null, to: null },
                        paymentSent: { from: null, to: null },
                        paymentDeposit: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {t('invoiceCentral.allDepositDates')}
                </label>
              </li>
              {/* Original Due Date */}
              <li>
                <label htmlFor="originalDueDate" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="originalDueDate"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'originalDueDate'}
                    onChange={() => {
                      setSelectedDate('originalDueDate');
                      // Clear other date filters when switching to original due date
                      handleFilters({
                        paymentSent: { from: null, to: null },
                        paymentDeposit: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
                </label>
                {selectedDate === 'originalDueDate' && (
                  <DateRangePicker
                    to={originalDueDate?.to ?? null}
                    from={originalDueDate?.from ?? null}
                    onFromChange={(date) => handleFilters({ originalDueDate: { from: date } })}
                    onToChange={(date) => handleFilters({ originalDueDate: { to: date } })}
                    toOpen={isToDatePickerOpen}
                    fromOpen={isFromDatePickerOpen}
                    onToOpenChange={setIsToDatePickerOpen}
                    onFromOpenChange={setIsFromDatePickerOpen}
                    name="Original Due Date"
                  />
                )}
              </li>
              {/* Early Payment Sent */}
              <li>
                <label htmlFor="paymentSent" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="paymentSent"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'paymentSent'}
                    onChange={() => {
                      setSelectedDate('paymentSent');
                      // Clear other date filters when switching to payment sent
                      handleFilters({
                        originalDueDate: { from: null, to: null },
                        paymentDeposit: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {t('invoiceCentral.earlyPaymentSent')}
                </label>
                {selectedDate === 'paymentSent' && (
                  <DateRangePicker
                    to={paymentSent?.to ?? null}
                    from={paymentSent?.from ?? null}
                    onFromChange={(date) => handleFilters({ paymentSent: { from: date } })}
                    onToChange={(date) => handleFilters({ paymentSent: { to: date } })}
                    toOpen={isToDatePickerOpen}
                    fromOpen={isFromDatePickerOpen}
                    onToOpenChange={setIsToDatePickerOpen}
                    onFromOpenChange={setIsFromDatePickerOpen}
                    name="Early Payment Sent"
                  />
                )}
              </li>
              {/* Estimated Deposit Date */}
              <li>
                <label htmlFor="paymentDeposit" className="flex cursor-pointer items-center gap-2">
                  <input
                    id="paymentDeposit"
                    className="h-5 w-5 accent-secondary-500 focus:ring-secondary-600"
                    checked={selectedDate === 'paymentDeposit'}
                    onChange={() => {
                      setSelectedDate('paymentDeposit');
                      // Clear other date filters when switching to payment deposit
                      handleFilters({
                        originalDueDate: { from: null, to: null },
                        paymentSent: { from: null, to: null },
                      });
                    }}
                    type="radio"
                  />
                  {t('draftOffer.labels.estimatedDepositDate')}
                </label>
                {selectedDate === 'paymentDeposit' && (
                  <DateRangePicker
                    to={paymentDeposit?.to ?? null}
                    from={paymentDeposit?.from ?? null}
                    onFromChange={(date) => handleFilters({ paymentDeposit: { from: date } })}
                    onToChange={(date) => handleFilters({ paymentDeposit: { to: date } })}
                    toOpen={isToDatePickerOpen}
                    fromOpen={isFromDatePickerOpen}
                    onToOpenChange={setIsToDatePickerOpen}
                    onFromOpenChange={setIsFromDatePickerOpen}
                    name="Estimated Deposit Date"
                  />
                )}
              </li>
            </ul>
          </div>
        </div>
        {/* Invoice Amount Filter */}
        <div className="w-full space-y-1">
          <Label>{t(`core.invoiceAmount`)}</Label>
          <div className="space-y-2 rounded border border-stroke bg-canvas p-4">
            <div>
              <Label htmlFor="from" className="capitalize">
                {t(`core.from`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="from"
                  name="from"
                  onChange={(e) => {
                    if (validateNumber(e.target.value)) {
                      setAmountFromDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountFromDebounce}
                />
                {currency}
              </div>
            </div>
            <div>
              <Label htmlFor="to" className="capitalize">
                {t(`core.to`)}
              </Label>
              <div className="flex items-center gap-2">
                <TextInput
                  id="to"
                  name="to"
                  onChange={(e) => {
                    if (validateNumber(e.target.value)) {
                      setAmountToDebounce(e.target.value);
                    }
                  }}
                  size="sm"
                  type="text"
                  value={amountToDebounce}
                />
                {currency}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default EligibleInvoiceFilters;
